// istanbul ignore file
import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider
} from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { type PropsWithChildren } from 'react';

import { REDIRECT_WITH_ACCOUNT_DETAILS } from 'reservations/base/constants/redirects';
import is5xxError from 'reservations/base/helpers/is5xxError';
import { useErrorSnackbar } from 'reservations/base/hooks/useSnackbar';
import { useLoadingSpinner } from 'reservations/base/providers/LoadingSpinnerProvider';
import { useCustomRouter } from 'reservations/base/providers/RouterProvider';

const GENERIC_ERROR_MESSAGE =
  'Something went wrong. Please try again or contact support.';

const isUnreadableMessage = (message: string) =>
  !message ||
  message === 'None' ||
  message === 'null' ||
  /^\d{3}:?\s*$/.test(message);

const getReadableErrorMessage = (errorMessage: string) => {
  if (isUnreadableMessage(errorMessage)) {
    return GENERIC_ERROR_MESSAGE;
  }

  switch (errorMessage) {
    case 'Account diner already exists':
      return 'Please enter the verification code to continue.';
    default:
      return errorMessage;
  }
};

const QueryProvider = ({ children }: PropsWithChildren) => {
  const { handleHasLoaded } = useLoadingSpinner();
  const enqueueErrorSnackbar = useErrorSnackbar();
  const { handleNavigation } = useCustomRouter();

  const client = new QueryClient({
    defaultOptions: {
      queries: {
        // NOTE: Disabling this because we're seeing unwanted redirection to 404 page on window refocus
        refetchOnWindowFocus: false
      }
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        // Error code exists on this error object, but isn't recognized on the type for some reason
        //@ts-ignore
        if (is5xxError(error.code)) {
          handleNavigation(REDIRECT_WITH_ACCOUNT_DETAILS);
          return;
        }

        if (
          query.meta?.onQueryError &&
          typeof query.meta.onQueryError === 'function'
        ) {
          query.meta.onQueryError(error);
        }

        //@ts-ignore
        if (is5xxError(error.code)) {
          handleNavigation(REDIRECT_WITH_ACCOUNT_DETAILS);
          return;
        }
      },
      onSuccess: (data, query) => {
        if (
          query.meta?.onQuerySuccess &&
          typeof query.meta.onQuerySuccess === 'function'
        ) {
          query.meta.onQuerySuccess(data);
        }
      }
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        const mutationKey = (mutation.options.mutationKey?.[0] as string) ?? '';

        enqueueErrorSnackbar(
          mutationKey,
          getReadableErrorMessage(error.message)
        );
      },
      onSettled: (_data, _error, _variables, _context, _mutation) => {
        handleHasLoaded();
      },
      onSuccess: (_data, _variables, _context, mutation) => {
        const mutationKey = (mutation.options.mutationKey?.[0] as string) ?? '';

        // Excluding join booking as this creates a weird flash of the "party is full" screen when
        // the last guest joins
        if (mutationKey !== 'JOIN_BOOKING') {
          client.invalidateQueries({
            queryKey: ['bentoboxReservations', 'booking']
          });
        }
        client.invalidateQueries({ queryKey: ['currentUser'] });
      }
    })
  });

  return (
    <QueryClientProvider client={client}>
      {children}
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
};

export default QueryProvider;
